import {
  ArrowForwardOutlined,
  DescriptionOutlined,
  GraphicEqOutlined,
  PlaylistAddCheckOutlined,
  ReportProblemOutlined,
  SummarizeOutlined,
} from '@mui/icons-material';
import { Box, Chip, Container, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';

const MotionBox = motion.create(Box);

const outputs = [
  {
    icon: <SummarizeOutlined />,
    label: '会议摘要',
    value: '提炼共识与关键结论',
    tone: 'brand',
  },
  {
    icon: <ReportProblemOutlined />,
    label: '风险清单',
    value: '识别待决事项与风险点',
    tone: 'accent',
  },
  {
    icon: <PlaylistAddCheckOutlined />,
    label: '行动项',
    value: '负责人、截止时间与下一步',
    tone: 'brand',
  },
];

const MeetingAssistant: React.FC = () => {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      id="meeting"
      component="section"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: 'rgb(var(--bg-rgb))',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, .86fr) minmax(400px, 1.14fr)' },
            gap: { xs: 5, lg: 7 },
            alignItems: 'center',
          }}
        >
          <MotionBox
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 1, y: 20 },
                  whileInView: { opacity: 1, y: 0 },
                  transition: { duration: 0.52 },
                  viewport: { once: true, amount: 0.35 },
                })}
          >
            <Chip
              icon={<GraphicEqOutlined />}
              label="Agent 办公辅助能力"
              sx={{
                mb: 2.2,
                color: 'rgb(var(--brand-rgb))',
                backgroundColor: 'rgba(var(--brand-rgb), 0.09)',
                border: '1px solid rgba(var(--brand-rgb), 0.13)',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                letterSpacing: '-0.045em',
                lineHeight: 1.12,
              }}
            >
              会议负责形成共识，
              <Box component="span" sx={{ display: 'block', color: 'rgb(var(--brand-rgb))' }}>
                Agent 负责继续推进。
              </Box>
            </Typography>
            <Typography sx={{ mt: 2.3, color: 'rgba(var(--ink-rgb), 0.64)', lineHeight: 1.85, maxWidth: 560 }}>
              会议是获得业务上下文的一种方式，而不是产品的中心。
              飞舟将会议中的关键结论整理成可追踪的材料和行动项，让后续工作自然接续。
            </Typography>
            <Box
              sx={{
                mt: 3,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.2,
                color: 'rgb(var(--accent-rgb))',
                fontWeight: 650,
                fontSize: '0.9rem',
              }}
            >
              从会议记录到业务行动
              <ArrowForwardOutlined fontSize="small" />
            </Box>
          </MotionBox>

          <MotionBox
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 1, x: 24 },
                  whileInView: { opacity: 1, x: 0 },
                  transition: { duration: 0.58, delay: 0.1 },
                  viewport: { once: true, amount: 0.3 },
                })}
            sx={{
              borderRadius: '28px',
              p: { xs: 2, sm: 3 },
              backgroundColor: 'rgb(var(--surface-rgb))',
              border: '1px solid rgba(var(--line-rgb), 0.09)',
              boxShadow: '0 24px 60px rgba(var(--shadow-rgb), 0.10)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                pb: 2,
                borderBottom: '1px solid rgba(var(--line-rgb), 0.09)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: 'rgba(var(--brand-rgb), 0.10)',
                    color: 'rgb(var(--brand-rgb))',
                  }}
                >
                  <DescriptionOutlined />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    项目协调会
                  </Typography>
                  <Typography sx={{ color: 'rgba(var(--ink-rgb), 0.48)', fontSize: '0.72rem' }}>
                    已整理为 1 份摘要 · 3 项行动
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label="待负责人确认"
                sx={{
                  color: 'rgb(var(--accent-rgb))',
                  backgroundColor: 'rgba(var(--accent-rgb), 0.10)',
                }}
              />
            </Box>

            <Box sx={{ mt: 2, p: 2, borderRadius: '16px', backgroundColor: 'rgb(var(--surface-soft-rgb))' }}>
              <Typography
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.64rem',
                  letterSpacing: '0.12em',
                  color: 'rgb(var(--brand-rgb))',
                  mb: 0.8,
                }}
              >
                AGENT SUMMARY
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.75, color: 'rgba(var(--ink-rgb), 0.76)' }}>
                本周重点完成机电深化节点确认；消防审查资料仍需补齐两项；
                施工图交付时间暂按周五推进，等待设计负责人确认。
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 1, mt: 1.5 }}>
              {outputs.map((output) => (
                <Box
                  key={output.label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '34px minmax(90px, .45fr) minmax(0, 1fr)',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.15,
                    borderRadius: '13px',
                    border: '1px solid rgba(var(--line-rgb), 0.07)',
                  }}
                >
                  <Box
                    sx={{
                      color:
                        output.tone === 'accent'
                          ? 'rgb(var(--accent-rgb))'
                          : 'rgb(var(--brand-rgb))',
                      display: 'flex',
                    }}
                  >
                    {output.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 650, fontSize: '0.8rem' }}>{output.label}</Typography>
                  <Typography sx={{ color: 'rgba(var(--ink-rgb), 0.53)', fontSize: '0.76rem' }}>
                    {output.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
};

export default MeetingAssistant;
